import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Printer, Zap } from 'lucide-react';
import { Worker } from '../../types';

interface QRBadgeModalProps {
  worker: Worker;
  onClose: () => void;
}

export const QRBadgeModal: React.FC<QRBadgeModalProps> = ({ worker, onClose }) => {
  const handlePrint = () => {
    window.print();
  };

  const getRoleLabel = (w: Worker) => {
    if (w.roleHierarchy === 'foreman') return '👑 Форемен (Прораб)';
    if (w.roleHierarchy === 'brigadier') return '👨‍✈️ Бригадир';
    if (w.roleHierarchy === 'helper') return '🦺 Помощник';
    return '🛠️ Мастер';
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl space-y-4 animate-scale-in">
        {/* Header */}
        <div className="bg-slate-50 px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <span className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
            <Zap className="w-4 h-4 text-amber-500 fill-current" />
            Электронный пропуск / Бейдж
          </span>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-200 transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Printable Badge Card */}
        <div className="p-5 text-center space-y-4">
          <div className="bg-gradient-to-b from-slate-50 to-slate-100 p-5 rounded-2xl border-2 border-amber-400/60 shadow-md space-y-4">
            <div className="flex items-center justify-center gap-1.5 text-xs font-black text-amber-800">
              <Zap className="w-4 h-4 fill-current text-amber-500" />
              <span>ООО «ЭлектроМонтажСтрой»</span>
            </div>

            {/* QR Code */}
            <div className="bg-white p-3.5 rounded-2xl inline-block shadow-md border border-slate-200">
              <QRCodeSVG
                value={`EM-WORKER:${worker.id}:${worker.qrCodeHash}`}
                size={140}
                level="H"
              />
            </div>

            <div className="space-y-1">
              <h3 className="font-black text-slate-900 text-base">{worker.fullName}</h3>
              <p className="text-xs text-amber-800 font-bold">{getRoleLabel(worker)}</p>
              <p className="text-[11px] text-slate-500 font-medium">{worker.team_name || 'Звено ЭОМ'}</p>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-200 text-[11px] text-slate-700">
              <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
                <span className="text-slate-400 block text-[9px] uppercase font-bold">Патент</span>
                <strong className="text-slate-800 font-mono font-bold">{worker.patentNumber || 'ПАТ-2026'}</strong>
              </div>
              <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
                <span className="text-slate-400 block text-[9px] uppercase font-bold">Регистрация до</span>
                <strong className="text-emerald-700 font-bold">{worker.registrationExpiryDate || '2027-01-01'}</strong>
              </div>
            </div>

            <div className="text-[10px] text-slate-500 font-medium">
              Объект: ЖК «Северная Звезда»
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handlePrint}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl text-xs font-bold shadow-md shadow-blue-600/20 transition active:scale-95"
            >
              <Printer className="w-4 h-4" />
              <span>Распечатать пропуск</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
